import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import jsQR from 'jsqr';
import {
  AlertCircle,
  Camera,
  CameraOff,
  Check,
  CheckCircle2,
  Hourglass,
  Loader2,
  Lock,
  Minus,
  Plus,
  QrCode,
  ShieldAlert,
  Ticket,
  UserCheck,
  Clock,
} from 'lucide-react';
import { lireRetraitRepas, validerRetraitRepas } from '../services/api';
import { useAuth } from '../hooks/useAuth';

/** Horodatage d'une remise : la minute compte pour trancher un litige. */
const dateRemise = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

const dateService = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Remise des tickets de repas achetés en ligne.
 *
 * Le convive présente le QR code reçu par e-mail ; un membre du bureau le
 * scanne, contrôle le nombre de couverts et remet les tickets papier. La
 * remise peut se faire en plusieurs fois — une famille arrive rarement
 * groupée.
 *
 * Le décodage se fait dans le navigateur : la PWA fonctionne sur le terrain,
 * où le réseau d'une salle des fêtes est ce qu'il est.
 */
export default function RetraitRepasPage() {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  const estBureau =
    user?.roles?.includes('ROLE_BUREAU') === true || user?.roles?.includes('ROLE_ADMIN') === true;

  if (authLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!estBureau) {
    return (
      <div className="p-6 lg:p-10 text-center space-y-3 pb-24 lg:pb-0">
        <Lock className="w-12 h-12 mx-auto text-gray-300" />
        <h1 className="text-xl font-bold">Accès réservé</h1>
        <p className="text-gray-600 dark:text-gray-400">
          La remise des tickets est réservée aux membres du bureau.
        </p>
      </div>
    );
  }

  return token ? (
    <FicheRetrait token={token} onRescanner={() => navigate('/retrait-repas')} />
  ) : (
    <Scanner onCode={(t) => navigate(`/retrait-repas/${t}`)} />
  );
}

/* --------------------------------- Scanner --------------------------------- */

function Scanner({ onCode }: { onCode: (token: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  // Sur téléphone, la caméra s'ouvre d'elle-même : on tient déjà l'appareil
  // du convive, un clic de plus fait perdre le geste. Sur ordinateur c'est
  // l'inverse — la webcam est rarement orientable vers un téléphone, et la
  // saisie du code est le geste courant. Elle passe donc devant, la webcam
  // restant à un clic.
  const surMobile =
    typeof navigator !== 'undefined' &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const [actif, setActif] = useState(surMobile);
  const [erreur, setErreur] = useState<string | null>(null);
  const [saisie, setSaisie] = useState('');

  useEffect(() => {
    if (!actif) return;

    let flux: MediaStream | null = null;
    let animation = 0;
    let arrete = false;

    const lire = () => {
      const v = video.current;
      const c = canvas.current;

      if (!arrete && v && c && v.readyState === v.HAVE_ENOUGH_DATA) {
        c.width = v.videoWidth;
        c.height = v.videoHeight;

        const contexte = c.getContext('2d', { willReadFrequently: true });
        if (contexte) {
          contexte.drawImage(v, 0, 0, c.width, c.height);
          const image = contexte.getImageData(0, 0, c.width, c.height);
          const code = jsQR(image.data, image.width, image.height, {
            inversionAttempts: 'dontInvert',
          });

          if (code?.data) {
            arrete = true;
            // Le QR ne porte que le jeton, sans adresse ni donnée
            // personnelle : il se transmet tel quel.
            const jeton = code.data.trim();
            if (jeton !== '') {
              onCode(jeton);
              return;
            }
            arrete = false;
          }
        }
      }

      if (!arrete) animation = requestAnimationFrame(lire);
    };

    navigator.mediaDevices
      // La caméra arrière : celle qu'on pointe vers le téléphone du convive.
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        flux = s;
        if (video.current) {
          video.current.srcObject = s;
          video.current.setAttribute('playsinline', 'true');
          void video.current.play();
        }
        animation = requestAnimationFrame(lire);
      })
      .catch(() => {
        setErreur(
          "L'accès à la caméra a été refusé. Autorisez-le dans les réglages du navigateur, " +
            'ou saisissez le code à la main.',
        );
        setActif(false);
      });

    return () => {
      arrete = true;
      cancelAnimationFrame(animation);
      flux?.getTracks().forEach((piste) => piste.stop());
    };
  }, [actif, onCode]);

  return (
    <div className="p-4 lg:p-0 flex flex-col gap-4 pb-24 lg:pb-0 max-w-lg mx-auto">
      <header className="space-y-1 order-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <QrCode className="w-6 h-6 text-primary-600" />
          Remise des tickets
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {surMobile
            ? 'Scannez le QR code du convive pour lui remettre ses tickets.'
            : 'Saisissez le code du convive, ou ouvrez la webcam pour scanner son QR code.'}
        </p>
      </header>

      {erreur && (
        <p className="text-sm rounded-xl px-3 py-2 bg-amber-50 text-amber-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {erreur}
        </p>
      )}

      {/* Saisie du code : en tête sur ordinateur, en secours sur téléphone. */}
      <form
        className={`card space-y-2 ${surMobile ? 'order-2' : ''}`}
        onSubmit={(event) => {
          event.preventDefault();
          if (saisie.trim()) onCode(saisie.trim());
        }}
      >
        <p className="text-sm font-medium">
          {surMobile ? 'Le code ne se lit pas ?' : 'Code de retrait du convive'}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Il figure sous le QR code, dans l'e-mail de confirmation.
        </p>
        <div className="flex gap-2">
          <input
            className="input flex-1 font-mono uppercase tracking-wider"
            placeholder="F2ET5-TPF9F"
            autoComplete="off"
            autoCapitalize="characters"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
          />
          <button type="submit" disabled={saisie.trim() === ''} className="btn-primary disabled:opacity-50">
            Ouvrir
          </button>
        </div>
      </form>

      <div className={`card p-0 overflow-hidden ${surMobile ? 'order-1' : ''}`}>
        <div className="relative aspect-square bg-gray-900">
          <video ref={video} className="w-full h-full object-cover" muted playsInline />
          <canvas ref={canvas} className="hidden" />

          {!actif && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white px-6 text-center">
              <CameraOff className="w-10 h-10 opacity-60" />
              <p className="text-sm opacity-80">
                {surMobile
                  ? 'Caméra arrêtée.'
                  : 'Vous pouvez aussi présenter le QR code devant la webcam.'}
              </p>
              <button
                onClick={() => {
                  setErreur(null);
                  setActif(true);
                }}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Camera className="w-4 h-4" />
                {surMobile ? 'Activer la caméra' : 'Ouvrir la webcam'}
              </button>
            </div>
          )}

          {actif && (
            // Repère de visée : sans lui, on ne sait pas où présenter le code.
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-2/3 aspect-square border-4 border-white/70 rounded-2xl" />
            </div>
          )}
        </div>
      </div>

      {actif && (
        <button
          onClick={() => setActif(false)}
          className={`btn-outline w-full justify-center ${surMobile ? 'order-1' : ''}`}
        >
          Arrêter la caméra
        </button>
      )}
    </div>
  );
}

/* ------------------------------ Fiche de retrait ---------------------------- */

function FicheRetrait({ token, onRescanner }: { token: string; onRescanner: () => void }) {
  const [nombre, setNombre] = useState(0);
  // Un repas se remet catégorie par catégorie : un ticket adulte et un ticket
  // enfant ne donnent pas droit à la même part.
  const [adultes, setAdultes] = useState(0);
  const [enfants, setEnfants] = useState(0);
  const [notice, setNotice] = useState<{ texte: string; complete: boolean } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['retrait-repas', token],
    queryFn: () => lireRetraitRepas(token),
    retry: false,
  });

  const reservation = data?.reservation;
  // Le même scanner sert les deux prestations : le serveur dit laquelle il a
  // trouvée, et seuls les mots changent — tickets remis, ou entrées validées.
  const estConcert = reservation?.type === 'concert';

  // Par défaut on remet tout : c'est le cas courant, et le membre du bureau
  // ajuste seulement quand la famille arrive en plusieurs fois.
  useEffect(() => {
    if (!reservation) return;
    setNombre(reservation.ticketsRestants);
    setAdultes(reservation.restantsAdulte ?? 0);
    setEnfants(reservation.restantsEnfant ?? 0);
  }, [reservation]);

  const valider = useMutation({
    mutationFn: () =>
      estConcert
        ? validerRetraitRepas(token, nombre)
        : validerRetraitRepas(token, { nbAdultes: adultes, nbEnfants: enfants }),
    onSuccess: (d) => {
      setNotice({ texte: d.message, complete: d.complete === true });
      setErreur(null);
      refetch();
    },
    onError: (e: Error) => setErreur(e.message),
  });

  const totalDemande = estConcert ? nombre : adultes + enfants;

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="p-6 space-y-4 text-center max-w-lg mx-auto">
        <AlertCircle className="w-12 h-12 mx-auto text-red-300" />
        <p className="text-red-600">{(error as Error)?.message ?? 'Réservation introuvable.'}</p>
        <button onClick={onRescanner} className="btn-primary inline-flex items-center gap-2">
          <QrCode className="w-4 h-4" /> Scanner un autre code
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-4 pb-24 lg:pb-0 max-w-lg mx-auto">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Ticket className="w-6 h-6 text-primary-600" />
          {reservation.nom || (estConcert ? 'Spectateur' : 'Convive')}
        </h1>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {reservation.codeRetrait && (
            <span className="font-mono font-semibold tracking-wider px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100">
              {reservation.codeRetrait}
            </span>
          )}
          <span className="font-mono text-gray-500 dark:text-gray-400">{reservation.reference}</span>
        </div>
      </header>

      {/* La remise complète solde la réservation, la remise partielle laisse
          du monde à servir : les deux ne doivent pas se ressembler, sinon le
          bureau range le carnet trop tôt. */}
      {notice && (
        <p
          className={`text-sm rounded-xl px-3 py-2 flex items-start gap-2 ${
            notice.complete
              ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
              : 'bg-sky-50 text-sky-900 dark:bg-sky-900/30 dark:text-sky-200'
          }`}
        >
          {notice.complete ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <Hourglass className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          {notice.texte}
        </p>
      )}
      {erreur && (
        <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {erreur}
        </p>
      )}

      {!reservation.payee && (
        <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">
          Cette réservation n'est pas payée. {estConcert ? 'Aucune entrée ne doit être validée.' : 'Aucun ticket ne doit être remis.'}
        </p>
      )}

      <section className="card space-y-3">
        {reservation.evenement && (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">{reservation.evenement.nom}</span>
            {reservation.evenement.date && (
              <span className="block text-xs text-gray-500 first-letter:uppercase dark:text-gray-400">
                {dateService.format(new Date(reservation.evenement.date))}
              </span>
            )}
          </p>
        )}

        <div className="grid grid-cols-3 gap-2 text-center">
          <Compteur
            libelle={estConcert ? 'Payantes' : 'Adultes'}
            valeur={estConcert ? reservation.nbPayantes : reservation.nbAdultes}
          />
          <Compteur
            libelle={estConcert ? 'Gratuites' : 'Enfants'}
            valeur={estConcert ? reservation.nbGratuites : reservation.nbEnfants}
          />
          <Compteur libelle="Total" valeur={reservation.nbTotal} accent="text-primary-700" />
        </div>

        <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
          <span className="text-gray-500 dark:text-gray-400">
            {estConcert ? 'Déjà validées' : 'Déjà remis'}
          </span>
          <span className="text-right">
            <span className="font-semibold tabular-nums">
              {reservation.ticketsRemis} / {reservation.nbTotal}
            </span>
            {/* Le détail dit lesquels : une famille servie « 3 sur 5 » n'a pas
                la même suite selon que les deux qui restent sont des enfants. */}
            {!estConcert && reservation.ticketsRemis > 0 && (
              <span className="block text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {reservation.remisAdulte} adulte{reservation.remisAdulte > 1 ? 's' : ''} ·{' '}
                {reservation.remisEnfant} enfant{reservation.remisEnfant > 1 ? 's' : ''}
              </span>
            )}
          </span>
        </div>

        {reservation.commentaire && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2">
            {reservation.commentaire}
          </p>
        )}
      </section>

      {reservation.soldee ? (
        // Avertissement franc : c'est la situation où un convive tente, de
        // bonne foi ou non, de retirer deux fois. Le membre du bureau doit le
        // voir sans lire, et pouvoir dire qui a servi et quand.
        <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-5 text-center space-y-3">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <ShieldAlert className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>

          <div>
            <p className="text-lg font-bold text-amber-900 dark:text-amber-200">
              {estConcert ? 'Entrées déjà validées' : 'Tickets déjà remis'}
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Les {reservation.nbTotal} {estConcert ? 'entrée' : 'ticket'}
              {reservation.nbTotal > 1 ? 's' : ''} de cette réservation ont déjà été
              {estConcert ? ' validées' : ' retirés'}. Ne servez pas une seconde fois.
            </p>
          </div>

          <dl className="text-sm bg-white/70 dark:bg-gray-900/40 rounded-xl p-3 space-y-1.5 text-left">
            {reservation.dernierRetraitPar && (
              <div className="flex items-start gap-2">
                <UserCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">{estConcert ? 'Validé par' : 'Remis par'}</dt>
                  <dd className="font-semibold text-gray-900 dark:text-gray-100">
                    {reservation.dernierRetraitPar}
                  </dd>
                </div>
              </div>
            )}
            {reservation.dernierRetraitLe && (
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">Le</dt>
                  <dd className="font-semibold text-gray-900 dark:text-gray-100 first-letter:uppercase">
                    {dateRemise.format(new Date(reservation.dernierRetraitLe))}
                  </dd>
                </div>
              </div>
            )}
          </dl>
        </div>
      ) : (
        reservation.payee && (
          <section className="card space-y-3">
            <p className="text-sm font-medium">
              {estConcert ? 'Combien d’entrées validez-vous ?' : 'Combien de tickets remettez-vous ?'}
            </p>

            {estConcert ? (
              <Selecteur
                valeur={nombre}
                max={reservation.ticketsRestants}
                onChange={setNombre}
                libelleMoins="Une entrée de moins"
                libellePlus="Une entrée de plus"
              />
            ) : (
              // Deux compteurs séparés : le bureau sert souvent trois adultes
              // maintenant et les enfants plus tard, et les deux tickets ne
              // donnent pas droit à la même part.
              <div className="space-y-3">
                <Selecteur
                  titre="Tickets adulte"
                  badge="bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
                  reste={reservation.restantsAdulte ?? 0}
                  valeur={adultes}
                  max={reservation.restantsAdulte ?? 0}
                  onChange={setAdultes}
                  libelleMoins="Un ticket adulte de moins"
                  libellePlus="Un ticket adulte de plus"
                />
                <Selecteur
                  titre="Tickets enfant"
                  badge="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                  reste={reservation.restantsEnfant ?? 0}
                  valeur={enfants}
                  max={reservation.restantsEnfant ?? 0}
                  onChange={setEnfants}
                  libelleMoins="Un ticket enfant de moins"
                  libellePlus="Un ticket enfant de plus"
                />
              </div>
            )}

            <p className="text-xs text-gray-500 text-center dark:text-gray-400">
              {reservation.ticketsRestants} {estConcert ? 'entrée' : 'ticket'}
              {reservation.ticketsRestants > 1 ? 's' : ''} restant
              {reservation.ticketsRestants > 1 ? 's' : ''} à {estConcert ? 'valider' : 'remettre'}.
            </p>

            <button
              onClick={() => valider.mutate()}
              disabled={valider.isPending || totalDemande < 1}
              className="btn-primary w-full justify-center py-3 disabled:opacity-50"
            >
              {valider.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Check className="w-5 h-5" />
              )}
              {estConcert ? 'Valider' : 'Valider la remise de'} {totalDemande}{' '}
              {estConcert ? 'entrée' : 'ticket'}
              {totalDemande > 1 ? 's' : ''}
            </button>
          </section>
        )
      )}

      <button onClick={onRescanner} className="btn-outline w-full justify-center inline-flex items-center gap-2">
        <QrCode className="w-4 h-4" /> Scanner un autre code
      </button>
    </div>
  );
}

/**
 * Compteur à gros boutons, utilisable d'une main dans le bruit.
 *
 * Le plus s'éteint sur le dernier ticket dû : mieux vaut l'empêcher que
 * refuser la remise après coup, devant le convive.
 */
function Selecteur({
  titre,
  badge,
  reste,
  valeur,
  max,
  onChange,
  libelleMoins,
  libellePlus,
}: {
  titre?: string;
  badge?: string;
  reste?: number;
  valeur: number;
  max: number;
  onChange: (v: number) => void;
  libelleMoins: string;
  libellePlus: string;
}) {
  return (
    <div className={titre ? 'rounded-xl border border-gray-100 dark:border-gray-800 p-3' : ''}>
      {titre && (
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge}`}>{titre}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            {reste} dû{(reste ?? 0) > 1 ? 's' : ''}
          </span>
        </div>
      )}

      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => onChange(Math.max(0, valeur - 1))}
          disabled={valeur <= 0}
          aria-label={libelleMoins}
          className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center disabled:opacity-30"
        >
          <Minus className="w-5 h-5" />
        </button>
        <span className="text-4xl font-bold tabular-nums w-16 text-center">{valeur}</span>
        <button
          onClick={() => onChange(Math.min(max, valeur + 1))}
          disabled={valeur >= max}
          aria-label={libellePlus}
          className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center disabled:opacity-30"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function Compteur({
  libelle,
  valeur,
  accent = 'text-gray-900 dark:text-gray-100',
}: {
  libelle: string;
  valeur: number;
  accent?: string;
}) {
  return (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-800 py-2">
      <p className="text-xs text-gray-500 dark:text-gray-400">{libelle}</p>
      <p className={`text-2xl font-bold tabular-nums ${accent}`}>{valeur}</p>
    </div>
  );
}
